/**
 * Recursively sorts all object keys alphabetically in a deep structure.
 *
 * This function traverses through nested objects and arrays, sorting the keys of each object
 * it encounters in alphabetical order. The original input structure is preserved, only the
 * key order is modified.
 *
 * @template T - The type of the input value
 * @param {T} input - The value to process (object, array, or primitive)
 * @returns {T} A new structure with all object keys sorted alphabetically at all levels
 *
 * @example
 * // Input: { z: 1, a: { y: 2, b: 3 } }
 * // Output: { a: { b: 3, y: 2 }, z: 1 }
 *
 * @example
 * // Works with arrays containing objects
 * // Input: [{ z: 1, a: 2 }]
 * // Output: [{ a: 2, z: 1 }]
 */
export default function sortKeysDeep<T>(input: T): T {
  if (Array.isArray(input)) {
    return input.map(sortKeysDeep) as unknown as T;
  }

  if (input !== null && typeof input === "object") {
    const sortedObject = Object.keys(input as Record<string, unknown>)
      .sort()
      .reduce(
        (acc, key) => {
          const value = (input as Record<string, unknown>)[key];
          (acc as Record<string, unknown>)[key] = sortKeysDeep(value);
          return acc;
        },
        {} as Record<string, unknown>,
      );

    return sortedObject as T;
  }

  return input;
}
